import { Product, isBundleProduct, isCompleteBundle } from "./Products";

import FoundryPhysicalProduct from "../foundry_client/models/FoundryPhysicalProduct";
import { PhysicalProductSubtype } from "../contentful_client/models/ContentfulResponseModels";
import { PhysicalProductsState } from "./PhysicalProducts";

const DEFAULT_FAMILY_STYLE_BUNDLE_DISCOUNT = 0.35;
const DEFAULT_FAMILY_COMPLETE_BUNDLE_DISCOUNT = 0.5;
export const LICENSE_BUNDLE_DISCOUNT = 0.25;

enum DiscountGroup {
  PackageDiscounts = "Package Discounts",
}

export type Discount = { discount: number; group: DiscountGroup };
export type DiscountFn = (product: Product, allProducts: Product[]) => Discount;

// This discount applies to individual family style bundle products.
const familyStyleBundleDiscount: DiscountFn = (product, allProducts) => {
  let discount = 0;
  if (isBundleProduct(product) && !isCompleteBundle(product)) {
    discount = DEFAULT_FAMILY_STYLE_BUNDLE_DISCOUNT;
  }

  return {
    group: DiscountGroup.PackageDiscounts,
    discount,
  };
};

// This discount applies when an entire family is purchased.
const familyCompleteBundleDiscount: DiscountFn = (product, allProducts) => {
  let discount = 0;
  if (isCompleteBundle(product)) {
    discount = DEFAULT_FAMILY_COMPLETE_BUNDLE_DISCOUNT;
  }

  return {
    group: DiscountGroup.PackageDiscounts,
    discount,
  };
};

const DiscountFns = [familyStyleBundleDiscount, familyCompleteBundleDiscount];

// Look for the discount that should be applied to a product based on all the products in the cart.
export const findDiscount = (
  product: Product,
  allProducts: Product[]
): Discount | null => {
  for (const discountFn of DiscountFns) {
    const calculatedDiscount = discountFn(product, allProducts);

    if (calculatedDiscount.discount > 0) {
      return calculatedDiscount;
    }
  }

  return null;
};

// A flat discount applied to each item of a physical product subtype, once a complete
// set of that subtype is in the cart. Items that don't complete a set are not discounted:
// three large prints discount the first two and leave the third at full price.
type SetDiscount = { setSize: number; discountCentsPerItem: number };

const SUBTYPE_SET_DISCOUNTS: { [subtype: string]: SetDiscount } = {
  [PhysicalProductSubtype.LargePrint]: {
    setSize: 2,
    discountCentsPerItem: 800,
  },
};

// The total discount in cents applied to each physical product in the cart, keyed by
// its Contentful ID. Products without a discount are absent from the map.
export type PhysicalProductDiscounts = { [contentfulId: string]: number };

// Look for the discounts that should be applied to the physical products in the cart.
// Set discounts are pooled across every product sharing a subtype, so two different
// large prints complete a set just as two of the same print would.
export const findPhysicalProductDiscounts = (
  physicalProductsState: PhysicalProductsState,
  physicalProductDetails: FoundryPhysicalProduct[]
): PhysicalProductDiscounts => {
  const discounts: PhysicalProductDiscounts = {};
  const entriesBySubtype: {
    [subtype: string]: { quantity: number; details: FoundryPhysicalProduct }[];
  } = {};

  physicalProductsState.forEach((product) => {
    const details = physicalProductDetails.find(
      (p) => p.id === product.contentfulId
    );

    if (!details?.subtype || !SUBTYPE_SET_DISCOUNTS[details.subtype]) {
      return;
    }

    const entries = entriesBySubtype[details.subtype] || [];
    entries.push({ quantity: product.quantity, details });
    entriesBySubtype[details.subtype] = entries;
  });

  Object.entries(entriesBySubtype).forEach(([subtype, entries]) => {
    const { setSize, discountCentsPerItem } = SUBTYPE_SET_DISCOUNTS[subtype];
    const totalQuantity = entries.reduce(
      (accum, entry) => accum + entry.quantity,
      0
    );

    // Only whole sets are discounted. Leftover items are charged at full price.
    let remaining = Math.floor(totalQuantity / setSize) * setSize;

    for (const entry of entries) {
      if (remaining <= 0) {
        break;
      }

      const discountedQuantity = Math.min(entry.quantity, remaining);
      // Never discount an item below free, in case a product is cheaper than the discount.
      const centsPerItem = Math.min(
        discountCentsPerItem,
        entry.details.priceCents
      );

      discounts[entry.details.id] =
        (discounts[entry.details.id] || 0) + discountedQuantity * centsPerItem;
      remaining -= discountedQuantity;
    }
  });

  return discounts;
};
